/**
 * Extracts the backend error body from a thrown action-kit error.
 *
 * Action wrappers in this feature throw the raw `response.error` object
 * (shape: { code, status, data, error }). The backend's domain error
 * payload — including `code` and feature-specific fields like
 * `conflicts: number[]` — lives at `response.error.data.data`. This
 * helper safely walks that path.
 */
function extractBackendErrorBody(
  error: unknown,
): Record<string, unknown> | undefined {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }
  const errAsAny = error as { data?: unknown };
  if (typeof errAsAny.data !== 'object' || errAsAny.data === null) {
    return undefined;
  }
  const httpData = errAsAny.data as { data?: unknown };
  if (typeof httpData.data !== 'object' || httpData.data === null) {
    return undefined;
  }
  return httpData.data as Record<string, unknown>;
}

export function extractBackendErrorCode(error: unknown): string | undefined {
  const body = extractBackendErrorBody(error);
  return body && typeof body.code === 'string' ? body.code : undefined;
}

/**
 * Extracts the `conflicts: number[]` array from a COURSE_GROUP_CYCLE
 * error. Returns an empty array if the field is missing or malformed.
 */
export function extractCycleConflicts(error: unknown): number[] {
  const body = extractBackendErrorBody(error);
  if (!body || !Array.isArray(body.conflicts)) {
    return [];
  }
  return body.conflicts.filter((id): id is number => typeof id === 'number');
}
